import type { ReactNode } from "react";
import type { WidgetFaceProps } from "@/components/shapes/shared/widget-face";
import {
  WIDGET_DISPLAY,
  WidgetEmpty,
  WidgetFaceRoot,
  WidgetMore,
} from "@/components/shapes/shared/widget-face-parts";
import { fitRows } from "@/components/shapes/shared/widget-rows";
import { useT } from "@/i18n";
import { framesGithubMessages } from "@/i18n/messages/frames-github";
import { cn } from "@/lib/cn";
import { ActionsSection, type ActionRun } from "./actions-section";
import { CommitsSection, MAX_VISIBLE_COMMITS } from "./commits-section";
import { commitsFrom, pullRequestsFrom } from "./github-content";
import { githubRepository } from "./github-repository";
import { PrSection } from "./pr-section";

/**
 * Two-line rows each size holds whole under its title and section labels:
 * one section on the small and medium faces (164 pt tall), two sharing the
 * rows on the large one (344 pt).
 */
const ROWS = { small: 2, medium: 2, large: 5 } as const;

/** The first rows of `items` that fit `rows` lines, then "+N more". */
function FittedRows<T>({
  items,
  rows,
  children,
}: {
  items: readonly T[];
  rows: number;
  children: (shown: T[]) => ReactNode;
}) {
  const { shown, more } = fitRows(items, rows);
  return (
    <>
      {children([...shown])}
      <WidgetMore count={more} className="px-2" />
    </>
  );
}

/**
 * A repository on the desktop: its pull requests, then its checks, then its
 * commits, the sections the frame shows, in whole rows; a row opens its pull
 * request, run or commit. A pocket row draws its text 8 pt inside its hover
 * plate, so the face's column is 8 pt wider on each side than the other
 * faces' and its title steps in to match: title, section labels and rows
 * all start 16 pt in, Apple's widget margin.
 */
export function GithubWidgetFace({ frame, size }: WidgetFaceProps) {
  const t = useT(framesGithubMessages);
  const prs = pullRequestsFrom(frame.content?.pr);
  const actions = (frame.content?.actions as ActionRun[] | undefined) ?? [];
  const commits = commitsFrom(frame.content?.commits);
  const repository = githubRepository([
    ...prs.map((pr) => pr.url),
    ...actions.map((run) => run.url),
  ]);

  const lists = [
    {
      id: "prs",
      count: prs.length,
      render: (rows: number) => (
        <FittedRows items={prs} rows={rows}>
          {(shown) => <PrSection prs={shown} />}
        </FittedRows>
      ),
    },
    {
      id: "checks",
      count: actions.length,
      render: (rows: number) => (
        <FittedRows items={actions} rows={rows}>
          {(shown) => <ActionsSection actions={shown} />}
        </FittedRows>
      ),
    },
    {
      id: "commits",
      count: commits.length,
      // Never past the section's cap, where it would add its own "Show more".
      render: (rows: number) => (
        <FittedRows items={commits} rows={Math.min(rows, MAX_VISIBLE_COMMITS)}>
          {(shown) => (
            <CommitsSection commits={shown} repositoryUrl={repository?.url} />
          )}
        </FittedRows>
      ),
    },
  ].filter((list) => list.count > 0);

  const [first, second] = lists;
  if (!first) {
    return <WidgetEmpty frame={frame} size={size} message={t("emptyState")} />;
  }
  const rows = ROWS[size];
  // On the large face the second section keeps up to half the rows and the
  // first takes the rest.
  const shared = size === "large" && second;
  const firstRows = shared
    ? Math.min(first.count, rows - Math.min(second.count, Math.floor(rows / 2)))
    : rows;
  const title = repository?.name || frame.title.trim() || "GitHub";

  return (
    <WidgetFaceRoot frame={frame} size={size} className="px-2">
      <p
        className={cn("truncate px-2", WIDGET_DISPLAY.s)}
        data-testid="github-widget-title"
      >
        {title}
      </p>
      <div className="min-h-0">
        <div data-testid={`github-widget-${first.id}`}>
          {first.render(firstRows)}
        </div>
        {shared && (
          <div className="mt-3.5" data-testid={`github-widget-${second.id}`}>
            {second.render(rows - firstRows)}
          </div>
        )}
      </div>
    </WidgetFaceRoot>
  );
}
